import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { Download, OpenInNew } from "@mui/icons-material";
import { FormattedMessage } from "react-intl";
import { createExportedExcelFileName } from "../../utils/chatMediaUtils";

const GRAPH_MIN_HEIGHT = 300;
const GRAPH_DEFAULT_HEIGHT = 500;
const GRAPH_FIXED_WIDTH = 640;

export function SharedGraphEmbed({ graphUrl }) {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [retryKey, setRetryKey] = useState(0);
  const iframeRef = useRef(null);
  const timeoutRef = useRef(null);
  const settledRef = useRef(false);
  const retryCountRef = useRef(0);

  useEffect(() => {
    retryCountRef.current = 0;
  }, [graphUrl]);

  useEffect(() => {
    settledRef.current = false;
    setHasError(false);
    setIsLoading(true);
    timeoutRef.current = setTimeout(() => {
      if (settledRef.current) return;
      settledRef.current = true;
      setHasError(true);
      setIsLoading(false);
    }, 10000);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [graphUrl, retryKey]);

  useEffect(() => {
    if (!hasError) return undefined;
    const retryTimer = setTimeout(() => {
      if (retryCountRef.current >= 3) return;
      retryCountRef.current += 1;
      setRetryKey((k) => k + 1);
    }, 2000);
    return () => clearTimeout(retryTimer);
  }, [hasError]);

  const openInNewTab = useCallback(() => {
    window.open(graphUrl, "_blank", "noopener,noreferrer");
  }, [graphUrl]);

  const handleLoad = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (settledRef.current) return;
    setTimeout(() => {
      if (settledRef.current) return;
      settledRef.current = true;
      setIsLoading(false);
    }, 400);
  }, []);

  const handleError = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (settledRef.current) return;
    settledRef.current = true;
    setHasError(true);
    setIsLoading(false);
  }, []);

  if (!graphUrl) return null;

  if (hasError) {
    return (
      <Box className="shared-media-card shared-media-card--error">
        <Typography className="shared-media-card-text">
          <FormattedMessage
            id="chatbot.graphIframeError"
            defaultMessage="Could not load the chart. Open it in a new tab."
          />
        </Typography>
        <Button
          variant="contained"
          disableElevation
          startIcon={<OpenInNew />}
          onClick={openInNewTab}
          className="shared-media-btn"
        >
          <FormattedMessage
            id="chatbot.openGraphInNewTab"
            defaultMessage="Open chart"
          />
        </Button>
      </Box>
    );
  }

  return (
    <Box className="shared-media-block shared-media-graph">
      <Box className="shared-media-toolbar">
        <Button
          variant="outlined"
          size="small"
          startIcon={<OpenInNew />}
          onClick={openInNewTab}
          className="shared-media-btn-outline"
        >
          <FormattedMessage
            id="chatbot.openInNewTab"
            defaultMessage="Open in new tab"
          />
        </Button>
      </Box>
      <Box className="shared-media-frame">
        {isLoading && (
          <Box className="shared-media-loading">
            <CircularProgress size={36} sx={{ color: "var(--main-color, #0986ed)" }} />
          </Box>
        )}
        <iframe
          key={retryKey}
          ref={iframeRef}
          src={graphUrl}
          title="Graph"
          allowFullScreen
          allow="downloads"
          onLoad={handleLoad}
          onError={handleError}
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads"
          style={{
            width: "100%",
            minWidth: GRAPH_FIXED_WIDTH,
            height: Math.max(GRAPH_MIN_HEIGHT, GRAPH_DEFAULT_HEIGHT),
            minHeight: GRAPH_MIN_HEIGHT,
            border: "none",
            display: "block",
            backgroundColor: "#fff",
          }}
        />
      </Box>
    </Box>
  );
}

export function SharedExcelEmbed({ excelUrls }) {
  const urls = useMemo(
    () => (Array.isArray(excelUrls) ? excelUrls.filter(Boolean) : []),
    [excelUrls],
  );
  const firstUrl = urls[0];
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [srcIndex, setSrcIndex] = useState(0);
  const timeoutRef = useRef(null);

  useEffect(() => {
    setHasError(false);
    setIsLoading(true);
    setSrcIndex(0);
  }, [firstUrl]);

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setIsLoading(false);
    }, 10000);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [srcIndex, firstUrl]);

  const downloadExcelFile = useCallback(async (url, fileIndex = 0) => {
    if (!url) return;
    const finalFileName = createExportedExcelFileName({
      fileIndex,
      totalFiles: urls.length,
    });
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed with status ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = finalFileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Excel download failed, opening in new tab instead", error);
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, [urls.length]);

  if (!firstUrl) return null;

  const srcCandidates = [
    `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
      firstUrl,
    )}`,
    firstUrl,
  ];
  const iframeSrc = srcCandidates[Math.min(srcIndex, srcCandidates.length - 1)];

  const handleLoad = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsLoading(false);
  };

  const handleError = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (srcIndex === 0) {
      setSrcIndex(1);
      setIsLoading(true);
      return;
    }
    setHasError(true);
    setIsLoading(false);
  };

  const downloadButtons = urls.map((url, i) => (
    <Button
      key={`${url}-${i}`}
      variant={hasError ? "contained" : "outlined"}
      size="small"
      startIcon={<Download />}
      onClick={() => downloadExcelFile(url, i)}
      className={hasError ? "shared-media-btn" : "shared-media-btn-outline"}
      disableElevation={hasError}
    >
      {urls.length === 1 ? (
        <FormattedMessage
          id="chatbot.downloadExcelFile"
          defaultMessage="Download Excel file"
        />
      ) : (
        <FormattedMessage
          id="chatbot.downloadFileNumber"
          defaultMessage="Download file {number}"
          values={{ number: i + 1 }}
        />
      )}
    </Button>
  ));

  if (hasError) {
    return (
      <Box className="shared-media-card shared-media-card--error">
        <Typography className="shared-media-card-text">
          {urls.length === 1 ? (
            <FormattedMessage
              id="chatbot.excelReadyToDownload"
              defaultMessage="Excel file ready to download"
            />
          ) : (
            <FormattedMessage
              id="chatbot.excelFilesReadyToDownload"
              defaultMessage="{count} Excel files ready to download"
              values={{ count: urls.length }}
            />
          )}
        </Typography>
        <Box className="shared-media-actions">{downloadButtons}</Box>
      </Box>
    );
  }

  return (
    <Box className="shared-media-block shared-media-excel">
      <Box className="shared-media-toolbar shared-media-toolbar--start">
        {downloadButtons}
      </Box>
      <Box className="shared-media-frame shared-media-frame--excel">
        {isLoading && (
          <Box className="shared-media-loading">
            <CircularProgress size={36} sx={{ color: "var(--main-color, #0986ed)" }} />
          </Box>
        )}
        <iframe
          src={iframeSrc}
          title="Excel"
          onLoad={handleLoad}
          onError={handleError}
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          style={{
            width: "100%",
            minWidth: "100%",
            maxWidth: "100%",
            height: "400px",
            minHeight: 300,
            border: "none",
            display: "block",
            boxSizing: "border-box",
          }}
        />
        <span className="shared-excel-mask" aria-hidden />
      </Box>
    </Box>
  );
}

/**
 * Render ordered media embeds (graph / excel) for a shared answer.
 * Optionally include structured HTML graph via srcDoc.
 */
export function SharedAnswerMedia({ media = [], graphHtml = null }) {
  const items = [];

  if (graphHtml && String(graphHtml).trim()) {
    items.push(
      <Box key="graph-html" className="shared-media-block shared-media-graph">
        <Box className="shared-media-frame">
          <iframe
            title="Graph"
            srcDoc={String(graphHtml)}
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            style={{
              width: "100%",
              minWidth: GRAPH_FIXED_WIDTH,
              height: GRAPH_DEFAULT_HEIGHT,
              minHeight: GRAPH_MIN_HEIGHT,
              border: "none",
              display: "block",
              backgroundColor: "#fff",
            }}
          />
        </Box>
      </Box>,
    );
  }

  media.forEach((item, index) => {
    if (item?.type === "graph" && item.url) {
      items.push(
        <SharedGraphEmbed key={`graph-${index}-${item.url}`} graphUrl={item.url} />,
      );
    }
    if (item?.type === "excel" && item.url) {
      items.push(
        <SharedExcelEmbed
          key={`excel-${index}-${item.url}`}
          excelUrls={[item.url]}
        />,
      );
    }
  });

  if (!items.length) return null;
  return <Box className="shared-answer-media">{items}</Box>;
}

export default SharedAnswerMedia;
